/**
 * /university — landing page (M6 University).
 *
 * KPI strip + 4 destination tiles (Skills · Tuners · Training · Marketplace).
 * The marketplace tile is hidden for non-founder roles — the resolved
 * role comes from the auth store on first paint, no extra fetch.
 *
 * Numbers in the KPI strip share the same TanStack Query cache as the
 * deep pages, so navigation stays cheap (no refetch when the user has
 * already opened /university/skills earlier in the session).
 */
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowUpRight,
  BookOpenText,
  FlaskConical,
  GraduationCap,
  Loader2,
  type LucideIcon,
  Network,
  ShoppingBag,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react';
import * as React from 'react';
import { fmtCount, fmtRelative } from '../../../components/university/formatters';
import { learningApi, learningKeys } from '../../../lib/api/learning';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth-store';

interface TileMeta {
  to:
    | '/university/skills'
    | '/university/tuners'
    | '/university/training'
    | '/university/marketplace'
    | '/university/patterns'
    | '/university/ab-tests';
  title: string;
  description: string;
  Icon: LucideIcon;
  founderOnly?: boolean;
}

const TILES: TileMeta[] = [
  {
    to: '/university/skills',
    title: 'Skills',
    description: 'Versioned agent skills with effectiveness scores.',
    Icon: Sparkles,
  },
  {
    to: '/university/tuners',
    title: 'Tuners',
    description: 'Humans who train agents and approve skill updates.',
    Icon: Users,
  },
  {
    to: '/university/training',
    title: 'Training sessions',
    description: 'Agent draft → tuner correction → approved example.',
    Icon: Trophy,
  },
  {
    to: '/university/patterns',
    title: 'Patterns',
    description: 'Clusters of recurring corrections detected in attempts.',
    Icon: Network,
  },
  {
    to: '/university/ab-tests',
    title: 'A/B tests',
    description: 'Compare candidate prompt versions before promotion.',
    Icon: FlaskConical,
  },
  {
    to: '/university/marketplace',
    title: 'Marketplace',
    description: 'Browse, publish and install skills with redaction preview.',
    Icon: ShoppingBag,
  },
];

function UniversityHome() {
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const hasFounderAccess = role === 'founder' || role === 'admin';

  const summaryQ = useQuery({
    queryKey: learningKeys.summary(),
    queryFn: () => learningApi.getSummary(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const skillsQ = useQuery({
    queryKey: learningKeys.skillsList({}),
    queryFn: () => learningApi.listSkills({}),
    staleTime: 30_000,
  });
  const tunersQ = useQuery({
    queryKey: learningKeys.tunersList(),
    queryFn: () => learningApi.listTuners(),
    staleTime: 30_000,
  });
  const sessionsQ = useQuery({
    queryKey: learningKeys.trainingSessionsList({}),
    queryFn: () => learningApi.listTrainingSessions({}),
    staleTime: 30_000,
  });

  const stats = React.useMemo(() => {
    const fromSummary = summaryQ.data;
    return {
      skills: fromSummary?.activeSkillsCount ?? skillsQ.data?.skills.length ?? null,
      tuners: fromSummary?.tunersCount ?? tunersQ.data?.tuners.length ?? null,
      sessionsWeek: fromSummary?.trainingSessionsThisWeek ?? null,
      sessionsActive:
        fromSummary?.trainingSessionsActive ??
        sessionsQ.data?.sessions.filter(
          (s) => s.status === 'active' || s.status === 'awaiting_tuner',
        ).length ??
        null,
      latestExportAt: fromSummary?.latestExportAt ?? null,
      totalExports: fromSummary?.totalExports ?? null,
      abTestsActive: fromSummary?.abTestsActive ?? null,
      marketplacePublished: fromSummary?.marketplacePublishedCount ?? null,
    };
  }, [summaryQ.data, skillsQ.data, tunersQ.data, sessionsQ.data]);

  const isSyncing =
    summaryQ.isFetching || skillsQ.isFetching || tunersQ.isFetching || sessionsQ.isFetching;

  return (
    <section className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
      <header className="flex items-baseline gap-3">
        <GraduationCap className="h-6 w-6 text-foreground" aria-hidden />
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold tracking-tight text-foreground">University</h1>
          <p className="text-[12px] text-muted-foreground">
            Skills, tuners, training sessions. The CHAOS learning loop.
          </p>
        </div>
        {isSyncing ? (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> syncing…
          </span>
        ) : null}
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Active skills" value={stats.skills} icon={Sparkles} to="/university/skills" />
        <Kpi label="Tuners" value={stats.tuners} icon={Users} to="/university/tuners" />
        <Kpi
          label="Sessions / wk"
          value={stats.sessionsWeek}
          icon={Trophy}
          to="/university/training"
        />
        <Kpi
          label="Active sessions"
          value={stats.sessionsActive}
          icon={BookOpenText}
          to="/university/training"
        />
        <Kpi
          label="Open A/B"
          value={stats.abTestsActive}
          icon={FlaskConical}
          to="/university/ab-tests"
        />
        <Kpi
          label="Published"
          value={stats.marketplacePublished}
          icon={ShoppingBag}
          to="/university/marketplace"
          subtext={
            stats.totalExports != null ? `${fmtCount(stats.totalExports)} exports total` : undefined
          }
        />
      </div>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {TILES.filter((t) => !t.founderOnly || hasFounderAccess).map((tile) => (
          <Tile key={tile.to} tile={tile} />
        ))}
      </section>

      <footer className="rounded border border-border bg-background p-4 text-[12px] text-muted-foreground">
        Skills are versioned, scored by effectiveness, and improved through training sessions.
        {hasFounderAccess
          ? ' Founders can publish or sell licensed skills externally with built-in redaction.'
          : ''}
        {stats.latestExportAt ? ` Last export ${fmtRelative(stats.latestExportAt)}.` : ''}
      </footer>
    </section>
  );
}

interface KpiProps {
  label: string;
  value: number | null;
  icon: LucideIcon;
  to: TileMeta['to'];
  subtext?: string | undefined;
}

function Kpi({ label, value, icon: Icon, to, subtext }: KpiProps) {
  return (
    <Link
      to={to}
      className={cn(
        'group/kpi relative flex flex-col gap-1 rounded border border-border bg-background p-3 transition-colors hover:bg-muted/40',
      )}
    >
      <div className="flex items-center justify-between text-muted-foreground">
        <span className="text-[11px] uppercase tracking-wider">{label}</span>
        <Icon className="h-3.5 w-3.5" aria-hidden />
      </div>
      <span className="font-mono text-2xl font-semibold tabular-nums text-foreground">
        {value == null ? '—' : fmtCount(value)}
      </span>
      {subtext ? <span className="text-[11px] text-muted-foreground">{subtext}</span> : null}
    </Link>
  );
}

function Tile({ tile }: { tile: TileMeta }) {
  const Icon = tile.Icon;
  return (
    <Link
      to={tile.to}
      className="group/tile relative flex flex-col gap-2 rounded border border-border bg-background p-4 transition-colors hover:bg-muted/40"
    >
      <header className="flex items-center justify-between">
        <span className="flex h-7 w-7 items-center justify-center rounded bg-muted text-foreground">
          <Icon className="h-3.5 w-3.5" aria-hidden />
        </span>
        <ArrowUpRight
          className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover/tile:opacity-100"
          aria-hidden
        />
      </header>
      <h2 className="text-[13px] font-semibold text-foreground">{tile.title}</h2>
      <p className="text-[11px] text-muted-foreground">{tile.description}</p>
    </Link>
  );
}

export const Route = createFileRoute('/_authenticated/university/')({
  component: UniversityHome,
});
